import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Globe, Building2, MapPin, Users } from 'lucide-react';

const LiveStatsBar = ({
  allLocations = [],
  filteredLocations = [],
  className = ''
}) => {
  const stats = useMemo(() => {
    // Filtered stats
    const filtered = {
      total: filteredLocations.length,
      active: filteredLocations.filter(l => l.status === 'active').length,
      countries: filteredLocations.filter(l => l.type === 'country').length,
      continents: [...new Set(filteredLocations.map(l => l.continent))].length
    };

    // Total stats (for comparison)
    const total = {
      total: allLocations.length,
      active: allLocations.filter(l => l.status === 'active').length
    };

    return { filtered, total };
  }, [allLocations, filteredLocations]);

  const isFiltered = stats.filtered.total !== stats.total.total;

  return (
    <div className={`flex items-center gap-1 text-xs ${className}`}>
      {/* Location Count */}
      <motion.div
        key={stats.filtered.total}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg border border-gray-200"
      >
        <MapPin className="w-3.5 h-3.5 text-gray-400" />
        <span className="font-semibold text-gray-800">{stats.filtered.total}</span>
        <span className="text-gray-500">
          {isFiltered ? `of ${stats.total.total}` : 'locations'}
        </span>
      </motion.div>

      <span className="text-gray-300 mx-1">•</span>

      {/* Continents */}
      <motion.div
        key={stats.filtered.continents}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg border border-gray-200"
      >
        <Globe className="w-3.5 h-3.5 text-blue-500" />
        <span className="font-semibold text-gray-800">{stats.filtered.continents}</span>
        <span className="text-gray-500">continents</span>
      </motion.div>

      <span className="text-gray-300 mx-1">•</span>

      {/* Active Offices */}
      <motion.div
        key={stats.filtered.active}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg border border-gray-200"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span className="font-semibold text-gray-800">{stats.filtered.active}</span>
        <span className="text-gray-500">active</span>
      </motion.div>

      {/* Countries (hidden on mobile) */}
      <span className="text-gray-300 mx-1 hidden sm:inline">•</span>
      
      <motion.div
        key={stats.filtered.countries}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg border border-gray-200"
      >
        <Building2 className="w-3.5 h-3.5 text-gray-400" />
        <span className="font-semibold text-gray-800">{stats.filtered.countries}</span>
        <span className="text-gray-500">countries</span>
      </motion.div>
    </div>
  );
};

export default LiveStatsBar;
